/**
 * Gráfico de barras mensais — receita, custos e resultado líquido do DRE (competência).
 */

import React, { useMemo, useState, useEffect, useRef } from 'react'
import { BarChart3, ChevronDown, Check } from 'lucide-react'
import './MonthlyFinancialBarChart.css'

const SERIES_CONFIG = [
  { id: 'revenue', label: 'Receita Líquida', colorClass: 'revenue' },
  { id: 'expense', label: 'Custos e Despesas', colorClass: 'expense' },
  { id: 'profit', label: 'Resultado Líquido', colorClass: 'profit' }
]

const DEFAULT_VISIBLE = {
  revenue: true,
  expense: true,
  profit: true
}

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0
  }).format(value || 0)
}

function MonthlyFinancialBarChart({ data, formatValue = formatCurrency }) {
  const [hoveredIndex, setHoveredIndex] = useState(null)
  const [visibleSeries, setVisibleSeries] = useState(DEFAULT_VISIBLE)
  const [seriesMenuOpen, setSeriesMenuOpen] = useState(false)
  const seriesMenuRef = useRef(null)

  useEffect(() => {
    if (!seriesMenuOpen) return undefined

    const handleClickOutside = (event) => {
      if (seriesMenuRef.current && !seriesMenuRef.current.contains(event.target)) {
        setSeriesMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [seriesMenuOpen])

  const activeSeries = useMemo(
    () => SERIES_CONFIG.filter((s) => visibleSeries[s.id]),
    [visibleSeries]
  )

  const chart = useMemo(() => {
    if (!data?.months?.length || activeSeries.length === 0) return null

    const months = data.months.map((m, index) => ({ ...m, index }))
    const values = months.flatMap((m) =>
      activeSeries.map((s) => Math.abs(m[s.id] || 0))
    )
    const maxValue = Math.max(...values, 1)

    return {
      months,
      maxValue,
      bars: months.map((m) => ({
        ...m,
        series: activeSeries.map((s) => {
          const value = m[s.id] || 0
          return {
            ...s,
            value,
            heightPercent: (Math.abs(value) / maxValue) * 100,
            isNegative: s.id === 'profit' && value < 0
          }
        })
      }))
    }
  }, [data, activeSeries])

  const toggleSeries = (seriesId) => {
    setVisibleSeries((prev) => {
      const next = { ...prev, [seriesId]: !prev[seriesId] }
      const enabledCount = Object.values(next).filter(Boolean).length
      if (enabledCount === 0) return prev
      return next
    })
  }

  const seriesMenuLabel = useMemo(() => {
    const count = activeSeries.length
    if (count === SERIES_CONFIG.length) return 'Todas as séries'
    if (count === 1) return activeSeries[0].label
    return `${count} séries`
  }, [activeSeries])

  if (!data?.months?.length) {
    return (
      <div className="monthly-financial-chart monthly-financial-chart--empty">
        <p>Sem dados do DRE para o ano</p>
      </div>
    )
  }

  const hovered = hoveredIndex !== null && chart ? chart.bars[hoveredIndex] : null

  return (
    <div className="monthly-financial-chart">
      <div className="monthly-financial-chart-header">
        <div>
          <h3 className="monthly-financial-chart-title">
            <BarChart3 size={16} className="monthly-financial-chart-title-icon" />
            Resultado Mensal {data.year}
          </h3>
          <p className="monthly-financial-chart-subtitle">
            DRE por competência — receita líquida, CMV + despesas e resultado líquido
          </p>
        </div>
        <div className="monthly-financial-chart-header-actions">
          <div className="monthly-financial-chart-legend">
            {SERIES_CONFIG.map((s) => (
              visibleSeries[s.id] && (
                <span key={s.id} className="monthly-financial-legend-item">
                  <span className={`monthly-financial-legend-swatch ${s.colorClass}`} />
                  {s.label}
                </span>
              )
            ))}
          </div>
          <div className="monthly-financial-chart-series-picker" ref={seriesMenuRef}>
            <span className="monthly-financial-chart-series-label">Exibir:</span>
            <div className="monthly-financial-chart-series-dropdown">
              <button
                type="button"
                className="monthly-financial-chart-series-trigger"
                onClick={() => setSeriesMenuOpen((open) => !open)}
                aria-expanded={seriesMenuOpen}
                aria-haspopup="listbox"
              >
                {seriesMenuLabel}
                <ChevronDown
                  size={14}
                  className={`monthly-financial-chart-series-chevron${seriesMenuOpen ? ' is-open' : ''}`}
                  aria-hidden
                />
              </button>
              {seriesMenuOpen && (
                <ul className="balance-projection-chart-period-menu monthly-financial-chart-series-menu" role="listbox">
                  {SERIES_CONFIG.map((s) => (
                    <li key={s.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={visibleSeries[s.id]}
                        className={`balance-projection-chart-period-option monthly-financial-chart-series-option${visibleSeries[s.id] ? ' is-selected' : ''}`}
                        onClick={() => toggleSeries(s.id)}
                      >
                        <span className={`monthly-financial-legend-swatch ${s.colorClass}`} />
                        <span>{s.label}</span>
                        {visibleSeries[s.id] && <Check size={14} aria-hidden />}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="monthly-financial-chart-summary">
        {visibleSeries.revenue && (
          <div>
            <p className="monthly-financial-summary-label">Receita líquida no ano</p>
            <p className="monthly-financial-summary-value revenue">{formatValue(data.totals?.revenue)}</p>
          </div>
        )}
        {visibleSeries.expense && (
          <div>
            <p className="monthly-financial-summary-label">Custos e despesas no ano</p>
            <p className="monthly-financial-summary-value expense">{formatValue(data.totals?.expense)}</p>
          </div>
        )}
        {visibleSeries.profit && (
          <div>
            <p className="monthly-financial-summary-label">Resultado líquido no ano</p>
            <p className={`monthly-financial-summary-value profit${(data.totals?.profit || 0) < 0 ? ' negative' : ''}`}>
              {formatValue(data.totals?.profit)}
            </p>
          </div>
        )}
      </div>

      <div className="monthly-financial-chart-body">
        {!chart ? (
          <div className="monthly-financial-chart--empty-inline">
            <p>Selecione ao menos uma série para exibir</p>
          </div>
        ) : (
          <>
            <div className="monthly-financial-bars">
              {chart.bars.map((bar) => (
                <div
                  key={bar.month}
                  className={`monthly-financial-bar-col${bar.isCurrent ? ' is-current' : ''}${hoveredIndex === bar.index ? ' is-hovered' : ''}`}
                  onMouseEnter={() => setHoveredIndex(bar.index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  <div className="monthly-financial-bar-group">
                    {bar.series.map((s) => (
                      <div key={s.id} className="monthly-financial-bar-track">
                        <div
                          className={`monthly-financial-bar-fill ${s.colorClass}${s.isNegative ? ' negative' : ''}`}
                          style={{ height: `${Math.max(s.heightPercent, s.value !== 0 ? 6 : 0)}%` }}
                        />
                      </div>
                    ))}
                  </div>
                  <span className="monthly-financial-bar-label">{bar.label}</span>
                </div>
              ))}
            </div>

            {hovered && (
              <div className="monthly-financial-tooltip" role="tooltip">
                <p className="monthly-financial-tooltip-title">
                  {hovered.label} {data.year}
                  {hovered.isCurrent && ' (mês atual)'}
                </p>
                <div className="monthly-financial-tooltip-grid">
                  {visibleSeries.revenue && (
                    <div>
                      <span className="monthly-financial-tooltip-key revenue">Receita Líquida</span>
                      <span className="monthly-financial-tooltip-value">{formatValue(hovered.revenue)}</span>
                    </div>
                  )}
                  {visibleSeries.expense && (
                    <div>
                      <span className="monthly-financial-tooltip-key expense">Custos e Despesas</span>
                      <span className="monthly-financial-tooltip-value">{formatValue(hovered.expense)}</span>
                    </div>
                  )}
                  {visibleSeries.profit && (
                    <div>
                      <span className="monthly-financial-tooltip-key profit">Resultado Líquido</span>
                      <span className={`monthly-financial-tooltip-value${hovered.profit < 0 ? ' negative' : ''}`}>
                        {formatValue(hovered.profit)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {!hovered && (
              <p className="monthly-financial-chart-hint">Passe o mouse sobre um mês para ver os valores</p>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default MonthlyFinancialBarChart
